import { useEffect, useRef } from "react";

import type { ChatMessage as ChatMessageType } from "../../types/chat";
import { ChatMessage, ErrorMessage, TypingIndicator, RateLimitIndicator } from "./ChatMessage";

interface ConversationHistoryProps {
  messages: ChatMessageType[];
  userAvatar?: string;
  /** Persona avatar image path (e.g., "/images/akela.jpg") */
  personaAvatar?: string;
  /** Persona name for display */
  personaName?: string;
  isLoading?: boolean;
  error?: string | null;
  rateLimitCountdown?: number | null;
  /** When true, sources are collapsible (collapsed by default) */
  collapsibleSources?: boolean;
}

/**
 * Scrollable conversation history component
 */
export function ConversationHistory({
  messages,
  userAvatar,
  personaAvatar,
  personaName = "Akela",
  isLoading = false,
  error = null,
  rateLimitCountdown = null,
  collapsibleSources = false,
}: ConversationHistoryProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, isLoading, error, rateLimitCountdown]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-surface-dim">
      {messages.length === 0 && !isLoading && !error && !rateLimitCountdown && (
        <div className="flex items-center justify-center h-full text-center">
          <div>
            {personaAvatar ? (
              <img
                src={personaAvatar}
                alt={personaName}
                className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-4 border-primary-container"
              />
            ) : (
              <div className="text-6xl mb-4">🐺</div>
            )}
            <h2 className="text-2xl font-bold text-on-surface mb-2">Hoi! Ik ben {personaName}</h2>
            <p className="text-on-surface-variant">
              Stel me een vraag over de documenten van Scoutinggroep de Bevers
            </p>
          </div>
        </div>
      )}

      {messages.map((msg, index) => (
        <ChatMessage
          key={index}
          message={msg}
          userAvatar={userAvatar}
          personaAvatar={personaAvatar}
          collapsibleSources={collapsibleSources}
        />
      ))}

      {rateLimitCountdown !== null && (
        <RateLimitIndicator seconds={rateLimitCountdown} personaAvatar={personaAvatar} />
      )}
      {isLoading && !rateLimitCountdown && <TypingIndicator personaAvatar={personaAvatar} />}
      {error && <ErrorMessage message={error} personaAvatar={personaAvatar} />}
    </div>
  );
}
